import {
  computed,
  getCurrentInstance,
  nextTick,
  onMounted,
  ref,
  watch,
} from 'vue'
import { isClient, useTimeoutFn } from '@vueuse/core'

import {
  useId,
  useLockscreen,
  useZIndex
} from '@sgui-plus/hooks'
import { UPDATE_MODEL_EVENT } from '@sgui-plus/utils/constants'
import { isNumber } from '@vueuse/core'

import { createMinimize, removeMinimize } from './minimize'
import type { CSSProperties, Ref, SetupContext } from 'vue'
import type { ModalEmits, ModalProps } from './modal'
import { debugWarn } from '@sgui-plus/utils'

export const useModal = (
  props: ModalProps,
  targetRef: Ref<HTMLElement | undefined>
) => {
  const instance = getCurrentInstance()!
  const emit = instance.emit as SetupContext<ModalEmits>['emit']
  const { nextZIndex } = useZIndex()

  let lastPosition = ''
  const titleId = useId()
  const bodyId = useId()
  const visible = ref(false)
  const rendered = ref(false) // when desctroyOnClose is true, we initialize it as false vise versa
  const zIndex = ref(props.zIndex || nextZIndex())
  const isMax = ref(false)
  const isFull = ref(false)
  const isMini = ref(false)

  let openTimer: (() => void) | undefined = undefined
  let closeTimer: (() => void) | undefined = undefined

  const normalizeWidth = computed(() =>
    isNumber(props.width) ? `${props.width}px` : props.width
  )

  const normalizeHeight = computed(() =>
    isNumber(props.height) ? `${props.height}px` : props.height
  )

  const style = computed<CSSProperties>(() => {
    const style: CSSProperties = {}
    const varPrefix = `--sg-modal`
    if (!isMax.value && !isFull.value) {
      if (props.top) {
        style[`${varPrefix}-margin-top`] = props.top
      }
      if (props.left) {
        style[`${varPrefix}-margin-left`] = props.left
      }
      if (props.width) {
        style[`${varPrefix}-width`] = normalizeWidth.value
      }
      if (props.height) {
        style[`${varPrefix}-height`] = normalizeHeight.value
      }
    }
    return style
  })

  function afterEnter() {
    emit('open')
  }

  function afterLeave() {
    emit('hidden')
    emit(UPDATE_MODEL_EVENT, false)
    if (props.destroyOnClose) {
      rendered.value = false
    }
  }

  function beforeLeave() {
    // emit('cancel')
  }

  function open() {
    closeTimer?.()
    openTimer?.()

    if (props.openDelay && props.openDelay > 0) {
      ;({ stop: openTimer } = useTimeoutFn(() => doOpen(), props.openDelay))
    } else {
      doOpen()
    }
  }

  function close() {
    openTimer?.()
    closeTimer?.()

    if (props.closeDelay && props.closeDelay > 0) {
      ;({ stop: closeTimer } = useTimeoutFn(() => doClose(), props.closeDelay))
    } else {
      doClose()
    }
  }

  function handleOpen() {
    open()
    rendered.value = true // enables lazy rendering
    zIndex.value = props.zIndex ? zIndex.value++ : nextZIndex()
    nextTick(() => {
      if (targetRef.value) {
        targetRef.value.scrollTop = 0
      }
    })
  }

  function handleClose() {
    if (props.beforeCancel) {
      Promise.resolve(props.beforeCancel()).then((shouldCancel?: boolean) => {
        if (shouldCancel === false) {
          return
        }

        close()
      }).catch((err) => {
        debugWarn('SgModal', err)
      })
    } else {
      close()
    }
    emit('cancel')
  }

  function handleConfirm() {
    emit('ok')
  }

  function doOpen() {
    if (!isClient) return
    visible.value = true
  }

  function doClose() {
    visible.value = false
  }

  if (props.lockScroll) {
    useLockscreen(visible)
  }

  function onFocusoutPrevented(event: CustomEvent) {
    if (event.detail?.focusReason === 'pointer') {
      event.preventDefault()
    }
  }

  function onCloseRequested() {
    if (props.closeOnPressEscape) {
      handleClose()
    }
  }

  watch(
    () => props.modelValue,
    (val) => {
      if (val) {
        // 若当前弹窗已最小化，隐藏对应最小化状态框
        if (isMini.value) {
          removeMinimize(bodyId.value)
          doOpen()
        } else {
          handleOpen()
        }
      } else {
        // this.$el.removeEventListener('scroll', this.updatePopper
        if (visible.value) {
          close()
        }
      }
    }
  )

  /**
   * 监听窗口最大化的变动
   */
  watch(
    () => isMax.value,
    (val) => {
      if (!targetRef.value) return
      emit('maximize', val)
      if (val) {
        lastPosition = targetRef.value.style.transform
        targetRef.value.style.transform = ''
      } else {
        targetRef.value.style.transform = lastPosition
      }
    }
  )

  /**
   * 监听窗口最小化的变动
   */
  watch(
    () => isMini.value,
    (val) => {
      if (!targetRef.value) return
      emit('minimize', val)
      if (val) {
        doClose()
        createMinimize({
          id: bodyId.value,
          title: props.title,
          closable: props.closable,
          onRestore: () => {
            doOpen()
          },
          onClose: () => {
            handleClose()
          },
          onHide: () => {
            isMini.value = false
          }
        })
      }
    }
  )

  onMounted(() => {
    if (props.modelValue) {
      visible.value = true
      rendered.value = true // enables lazy rendering
      open()
    }
  })

  return {
    afterEnter,
    afterLeave,
    beforeLeave,
    handleClose,
    handleOpen,
    handleConfirm,
    close,
    onFocusoutPrevented,
    onCloseRequested,
    titleId,
    bodyId,
    style,
    rendered,
    visible,
    zIndex,
    isMax,
    isFull,
    isMini
  }
}
